import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {ReactComponent as WithdrawalCloseIcon} from '@/assets/icons/refer-earn/withdrawal-close.svg';
import {defaultCurrencySymbol} from '@/config';
import {
  cashArrowLeftImage,
  cashArrowRightImage,
  cashGiftImage,
  cashGiftOpenMaxImage,
  cashGiftOpenMinImage,
  congratulationBigCoinImage,
  congratulationShineImage,
  referSpinButtonImage,
  randomRewardCoinImage,
  sequentialCloseIconImage,
} from '../assets';
import {
  referEarnWhitePageBgImage,
  referEarnWhitePanelBgImage,
} from '../../../white-theme-assets';
import {
  CASH_GIFT_EFFECTS_FADE_MS,
  CASH_GIFT_GUIDE_DISMISS_MS,
  getWheelRules,
} from '../constants';
import {GuideButtonRect} from '../types';
import {
  formatDayCountdown,
  formatProgressAmount,
  getGuideButtonStyle,
  getRewardModalOverlayClassName,
  getRewardModalPanelClassName,
} from '../utils';
import {
  CashGiftRevealItem,
  CashGiftRevealPhase,
} from '../hooks/use-cash-gift-selection';
import {AutoFitSingleLineText} from './auto-fit-single-line-text';
import {CashGiftGuideMask} from './cash-gift-guide-mask';

const renderModalPortal = (node: React.ReactElement) => {
  if (typeof document === 'undefined') {
    return node;
  }
  return createPortal(node, document.body);
};

interface GuideModalProps {
  visible: boolean;
  canClose: boolean;
  guideButtonRect: GuideButtonRect | null;
  animationContainerRef: React.RefObject<HTMLDivElement>;
  onGuideButtonClick: () => void;
}

export function GuideModal({
  visible,
  canClose,
  guideButtonRect,
  animationContainerRef,
  onGuideButtonClick,
}: GuideModalProps) {
  if (!visible) {
    return null;
  }

  return renderModalPortal(
    <div className="fixed inset-0 z-[11140] bg-black/80">
      <div className="absolute" style={getGuideButtonStyle(guideButtonRect)}>
        <button
          type="button"
          onClick={onGuideButtonClick}
          disabled={!canClose}
          className="absolute inset-0 cursor-pointer border-0 bg-transparent p-0 disabled:cursor-default">
          <img
            src={referSpinButtonImage}
            alt=""
            className="h-full w-full object-contain"
          />
        </button>
        <div className="pointer-events-none absolute inset-0 rounded-full">
          <div
            ref={animationContainerRef}
            className="absolute left-1/2 top-1/2 h-[9rem] w-[9rem] -translate-x-[50%] -translate-y-[48%] pointer-events-none"
          />
        </div>
      </div>
    </div>,
  );
}

interface WheelRulesModalProps {
  visible: boolean;
  targetAmount: number;
  onClose: () => void;
}

export function WheelRulesModal({
  visible,
  targetAmount,
  onClose,
}: WheelRulesModalProps) {
  const {t} = useTranslation();
  const [mounted, setMounted] = useState(visible);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(frame);
    }
    setActive(false);
    const timer = window.setTimeout(() => setMounted(false), 180);
    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!mounted) {
    return null;
  }

  return renderModalPortal(
    <div
      className={`fixed inset-0 z-[11140] flex items-center justify-center overflow-hidden bg-black/75 px-[1.375rem] transition-opacity duration-200 ${
        active ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      onClick={onClose}>
      <div
        className={`relative h-[31.125rem] w-full max-w-[20.6875rem] overflow-hidden rounded-[1.25rem] px-[0.6875rem] pb-[1.25rem] pt-[1.1875rem] text-[#564715] transition-transform duration-200 ease-out ${
          active ? 'scale-100' : 'scale-[0.97]'
        }`}
        onClick={event => event.stopPropagation()}>
        <img
          src={referEarnWhitePanelBgImage}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
        <button
          type="button"
          aria-label={t('share.close')}
          onClick={onClose}
          className="absolute right-[0.6875rem] top-[1.03125rem] z-[1] flex h-[1.5rem] w-[1.5rem] items-center justify-center border-0 bg-transparent p-0 text-[#2B2419] active:scale-95">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            <path
              d="M6.75 6.75L17.25 17.25M17.25 6.75L6.75 17.25"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <h2 className="relative m-0 pb-3 font-inter text-[0.9375rem] font-semibold leading-[1.125rem] text-[#2B2419]">
          {t('invitation.wheel.rules')}
        </h2>
        <div className="relative h-px w-full bg-[rgba(44,44,49,0.1)]" />
        <div className="relative mt-[0.6875rem] max-h-[25.5rem] overflow-auto pr-[0.125rem] font-inter text-xs font-normal leading-[1.0425rem] text-[#564715]">
          {getWheelRules(targetAmount).map((rule, index) => (
            <p key={index} className="m-0 mb-[0.5rem]">
              {index + 1}、{rule}
            </p>
          ))}
        </div>
      </div>
    </div>,
  );
  /*
  const {t} = useTranslation();
  const [mounted, setMounted] = useState(visible);
  const [active, setActive] = useState(false);
  const wheelRules = getWheelRules(targetAmount);

  useEffect(() => {
    let frame = 0;
    let nextFrame = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (visible) {
      setActive(false);
      setMounted(true);
      frame = requestAnimationFrame(() => {
        nextFrame = requestAnimationFrame(() => {
          setActive(true);
        });
      });
    } else {
      setActive(false);
      timer = setTimeout(() => {
        setMounted(false);
      }, 200);
    }

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      if (nextFrame) {
        cancelAnimationFrame(nextFrame);
      }
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [visible]);

  if (!mounted) {
    return null;
  }

  return renderModalPortal(
    <div
      className={[
        'fixed inset-0 z-[11140] flex items-center justify-center overflow-hidden bg-black/75 px-[1.375rem] transition-opacity duration-200 ease-out',
        active ? 'opacity-100' : 'opacity-0',
      ].join(' ')}>
      <div
        className={[
          'relative h-[31.125rem] w-full max-w-[20.6875rem] overflow-hidden rounded-[1.25rem] px-[0.6875rem] pb-[1.25rem] pt-[1.1875rem] text-[#564715] transition-[opacity,transform] duration-200 ease-out',
          active
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-3 scale-[0.96] opacity-0',
        ].join(' ')}>
        <img
          src={referEarnWhitePanelBgImage}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
        <button
          type="button"
          aria-label={t('share.close')}
          onClick={onClose}
          className="absolute right-[0.6875rem] top-[1.03125rem] z-[1] flex h-[1.5rem] w-[1.5rem] cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[#2B2419] active:scale-95">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1.5rem"
            height="1.5rem"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            <path
              d="M17.25 6.75L6.75 17.25M6.75 6.75L17.25 17.25"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className="relative pb-[0.75rem] text-left font-inter text-[0.9375rem] font-semibold leading-[1.125rem] text-[#2B2419]">
          {t('invitation.wheel.rules')}
        </div>
        <div className="relative h-px w-full bg-[rgba(44,44,49,0.10)]" />
        <div className="relative mt-[0.6875rem] max-h-[25.5rem] overflow-auto pr-[0.125rem] font-inter text-xs font-normal leading-[1.0425rem] text-[#564715]">
          {wheelRules.map((rule, index) => (
            <p key={index} className="m-0 mb-[0.5rem]">
              {index + 1}、{rule}
            </p>
          ))}
        </div>
      </div>
    </div>,
  );
  */
}

interface SpecialDialogBottomModalProps {
  visible: boolean;
  rewardAmount: number;
  onClose: () => void;
}

export function SpecialDialogBottomModal({
  visible,
  rewardAmount,
  onClose,
}: SpecialDialogBottomModalProps) {
  const {t} = useTranslation();
  const [mounted, setMounted] = useState(visible);
  const [active, setActive] = useState(false);

  useEffect(() => {
    let frame = 0;
    let nextFrame = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (visible) {
      setActive(false);
      setMounted(true);
      frame = requestAnimationFrame(() => {
        nextFrame = requestAnimationFrame(() => {
          setActive(true);
        });
      });
    } else {
      setActive(false);
      timer = setTimeout(() => {
        setMounted(false);
      }, 200);
    }

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      if (nextFrame) {
        cancelAnimationFrame(nextFrame);
      }
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [visible]);

  if (!mounted) {
    return null;
  }

  return renderModalPortal(
    <div
      className={[
        'fixed inset-0 z-[11120] flex items-center justify-center overflow-hidden bg-black/75 px-[0.875rem] transition-opacity duration-200 ease-out',
        active ? 'opacity-100' : 'opacity-0',
      ].join(' ')}>
      <div
        className={[
          'flex w-full flex-col items-center transition-[opacity,transform] duration-200 ease-out',
          active
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-3 scale-[0.96] opacity-0',
        ].join(' ')}>
        <div className="relative h-[14.25rem] w-[18.75rem] overflow-hidden rounded-[1.25rem]">
          <img
            src={referEarnWhitePanelBgImage}
            alt=""
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
          <img
            src={randomRewardCoinImage}
            alt=""
            className="pointer-events-none absolute left-1/2 top-[2.75rem] h-[4.0625rem] w-16 -translate-x-1/2 object-contain"
            draggable={false}
          />
          <div className="pointer-events-none absolute left-1/2 top-[7.3125rem] w-[9.0625rem] -translate-x-1/2 text-center font-inter text-lg font-normal leading-[1.375rem] text-black/70">
            {t('invitation.wheel.randomRewards')}
          </div>
          <div className="pointer-events-none absolute left-1/2 top-[9.4375rem] w-[12rem] -translate-x-1/2 text-center font-inter text-2xl font-bold leading-[1.8125rem] text-[#6E3A02]">
            + {defaultCurrencySymbol} {formatProgressAmount(rewardAmount)}
          </div>
        </div>
        <button
          type="button"
          aria-label={t('share.close')}
          onClick={onClose}
          className="mt-4 flex h-11 w-11 cursor-pointer touch-manipulation items-center justify-center border-0 bg-transparent p-0 transition-opacity duration-200 active:opacity-80">
          <img
            src={sequentialCloseIconImage}
            alt=""
            aria-hidden="true"
            className="h-9 w-9 object-contain"
            draggable={false}
          />
        </button>
      </div>
    </div>,
  );
}

interface WithdrawalProgressModalProps {
  visible: boolean;
  closing: boolean;
  claimCountdownSeconds: number;
  displayedRewardAmount: number;
  withdrawalProgressPercent: number;
  targetAmount: number;
  freeSpinCount: number;
  onAction: () => void;
  onClose: () => void;
}

export function WithdrawalProgressModal({
  visible,
  closing,
  claimCountdownSeconds,
  displayedRewardAmount,
  withdrawalProgressPercent,
  targetAmount,
  freeSpinCount,
  onAction,
  onClose,
}: WithdrawalProgressModalProps) {
  const {t} = useTranslation();
  if (!visible && !closing) {
    return null;
  }

  return renderModalPortal(
    <div className={getRewardModalOverlayClassName(closing)} onClick={onClose}>
      <div
        className={`${getRewardModalPanelClassName(
          closing,
        )} !overflow-visible`}>
        <div className="absolute inset-0 -translate-y-[3.5rem]">
          <div className="absolute left-1/2 top-[8rem] w-[13rem] -translate-x-1/2 text-center font-inter text-[0.9375rem] font-bold leading-[1.125rem] capitalize text-white">
            {t('invitation.wheel.remainingWithdrawalTime')}
          </div>
          <div className="absolute left-1/2 top-[9.8125rem] w-full -translate-x-1/2 whitespace-nowrap px-3 text-center font-inter text-2xl font-bold leading-[1.8125rem] text-transparent bg-[linear-gradient(180deg,#FFA28F_34%,#FF898B_54%,#FF7577_77%)] bg-clip-text">
            {formatDayCountdown(claimCountdownSeconds)}
          </div>
          <img
            src={congratulationShineImage}
            alt=""
            className="refer-earn-reward-shine pointer-events-none absolute left-[-0.0625rem] top-[9.3125rem] h-[23.4375rem] w-[23.4375rem] object-contain"
          />
          <img
            src={congratulationBigCoinImage}
            alt=""
            className="pointer-events-none absolute left-1/2 top-[12.6875rem] h-[14.75rem] w-[19.625rem] -translate-x-1/2 object-contain drop-shadow-[0_0.4375rem_0.25rem_rgba(0,0,0,0.6)]"
          />
          <div className="absolute left-1/2 top-[26.9375rem] flex -translate-x-1/2 items-end justify-center gap-[0.375rem] text-center font-inter font-bold text-transparent bg-[linear-gradient(180deg,#FFE49A_31.25%,#FFA51F_64.19%,#FFD487_78.25%)] bg-clip-text drop-shadow-[0_0.1875rem_0_rgba(124,28,30,1)]">
            <span className="text-[2rem] leading-[2.3125rem]">
              {defaultCurrencySymbol}
            </span>
            <span className="text-[2.25rem] leading-[2.75rem]">
              {formatProgressAmount(displayedRewardAmount)}
            </span>
          </div>

          <div className="absolute left-3 right-3 top-[31.6875rem] h-[4.4375rem] rounded-[0.625rem] border border-[#F5D47A]/70 bg-[linear-gradient(180deg,#FFEDB7_0%,#FFECA5_100%)] px-[0.5625rem] pt-[1.0625rem] shadow-[0_0.25rem_0.75rem_rgba(116,60,5,0.18)]">
            <div className="text-center font-inter text-[0.9375rem] font-normal leading-[1.125rem] text-[#20160B]">
              {t('invitation.wheel.withdrawAfterCollecting')}{' '}
              <span className="font-bold text-[#FF4E4E]">{targetAmount}</span>
            </div>
            <div className="absolute bottom-[0.9375rem] left-[0.5625rem] right-[1rem] h-1 rounded-full bg-[#E4C87A]">
              <div
                className="h-full rounded-full bg-[linear-gradient(180deg,#FF9B44_0%,#FFCF4C_100%)] shadow-[0_0_0.5rem_rgba(255,183,45,0.32)]"
                style={{width: `${withdrawalProgressPercent}%`}}
              />
            </div>
          </div>

          <div className="refer-earn-withdraw-action absolute left-3 right-3 top-[39.0625rem]">
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onAction();
              }}
              className={[
                'flex h-[2.75rem] w-full items-center justify-center rounded-[0.625rem] border-0 p-0 font-inter text-lg font-semibold leading-none text-[#141828]',
                freeSpinCount > 0
                  ? 'bg-[linear-gradient(180deg,#FFE206_0%,#FFD45E_100%)] shadow-[0_0.25rem_0.125rem_rgba(255,206,30,0.4)]'
                  : 'animate-[refer-earn-invite-button-pulse_1.4s_ease-in-out_infinite] bg-[linear-gradient(180deg,#FFE206_0%,#FFD45E_100%)] shadow-[0_0.25rem_0.125rem_rgba(255,206,30,0.4)]',
              ].join(' ')}>
              {freeSpinCount > 0
                ? t('invitation.wheel.claimItNow')
                : t('invitation.wheel.inviteFriendsToGetFreeSpins')}
            </button>
          </div>
        </div>
        <button
          type="button"
          aria-label={t('share.close')}
          onClick={event => {
            event.stopPropagation();
            onClose();
          }}
          className="refer-earn-withdraw-close absolute left-1/2 top-[40.3125rem] z-10 flex h-8 w-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-solid border-white bg-transparent p-0 text-white active:scale-95">
          <WithdrawalCloseIcon
            aria-hidden="true"
            className="h-[0.625rem] w-[0.625rem]"
          />
        </button>
      </div>
    </div>,
  );
}

interface CongratulationModalProps {
  visible: boolean;
  closing: boolean;
  onClaim: () => void;
  targetAmount: number;
}

export function CongratulationModal({
  visible,
  closing,
  onClaim,
  targetAmount,
}: CongratulationModalProps) {
  const {t} = useTranslation();
  if (!visible && !closing) {
    return null;
  }

  return renderModalPortal(
    <div className={getRewardModalOverlayClassName(closing)}>
      <div className={getRewardModalPanelClassName(closing)}>
        <div className="absolute inset-0 -translate-y-[1.5rem]">
          <div className="absolute left-1/2 top-[7.6875rem] w-[21rem] max-w-[calc(100%-1.5rem)] -translate-x-1/2 whitespace-nowrap bg-[linear-gradient(180deg,#FFE49A_31.25%,#FFA51F_64.19%,#FFD487_78.25%)] bg-clip-text text-center font-inter text-[2rem] font-bold leading-[2.4375rem] text-transparent drop-shadow-[0_0.125rem_0.25rem_rgba(111,55,6,0.72)]">
            {t('invitation.wheel.congratulations')}
          </div>
          <div className="absolute left-1/2 top-[10.125rem] w-[13.5625rem] -translate-x-1/2 bg-[linear-gradient(180deg,#FFE49A_31.25%,#FFA51F_64.19%,#FFD487_78.25%)] bg-clip-text text-center font-inter text-base font-bold leading-[1.1875rem] text-transparent drop-shadow-[0_0.125rem_0.25rem_rgba(111,55,6,0.55)]">
            {t('invitation.wheel.bonusIssued')}
          </div>
          <img
            src={congratulationShineImage}
            alt=""
            className="refer-earn-reward-shine pointer-events-none absolute left-0 top-[9.625rem] h-[23.4375rem] w-[23.4375rem] object-contain"
          />
          <img
            src={congratulationBigCoinImage}
            alt=""
            className="refer-earn-achievement-coin pointer-events-none absolute left-1/2 top-[13rem] h-[14.75rem] w-[19.625rem] -translate-x-1/2 object-contain drop-shadow-[0_0.4375rem_0.25rem_rgba(0,0,0,0.6)]"
          />
          <div className="refer-earn-achievement-amount absolute left-1/2 top-[27.0625rem] flex -translate-x-1/2 items-end justify-center gap-[0.375rem] text-center font-inter font-bold text-transparent bg-[linear-gradient(180deg,#FFE49A_31.25%,#FFA51F_64.19%,#FFD487_78.25%)] bg-clip-text drop-shadow-[0_0.1875rem_0_rgba(124,28,30,1)]">
            <span className="text-[2rem] leading-[2.3125rem]">
              {defaultCurrencySymbol}
            </span>
            <span className="text-[2.25rem] leading-[2.75rem]">
              {targetAmount}
            </span>
          </div>
          <button
            type="button"
            onClick={onClaim}
            className="refer-earn-achievement-button absolute left-3 right-3 top-[34.3125rem] flex h-[2.75rem] items-center justify-center rounded-[0.625rem] border-0 bg-[linear-gradient(180deg,#FFE206_0%,#FFD45E_100%)] p-0 font-inter text-lg font-semibold leading-none text-[#141828] shadow-[0_0.1875rem_0.35625rem_rgba(244,184,35,0.32)]">
            {t('invitation.wheel.claimItNow')}
          </button>
        </div>
      </div>
    </div>,
  );
}

interface CashEverydayModalProps {
  visible: boolean;
  closing: boolean;
  targetAmount: number;
  selectedGiftIndex: number | null;
  cashGiftRevealPhase: CashGiftRevealPhase;
  cashGiftRevealItems: CashGiftRevealItem[];
  showCashGiftIdleEffects?: boolean;
  cashGiftGuideRef: React.RefObject<HTMLDivElement>;
  showCashGiftGuide: boolean;
  cashGiftGuideDismissing?: boolean;
  onChooseGift: (index: number) => void;
}

const CASH_GIFT_OPEN_AMOUNT_DELAY_MS = 360;

const getCashGiftAmountDelayMs = (
  index: number,
  phase: CashGiftRevealPhase,
  openedAt?: number,
) => {
  const flowDelay =
    phase === 'selectedOpen'
      ? CASH_GIFT_OPEN_AMOUNT_DELAY_MS
      : CASH_GIFT_OPEN_AMOUNT_DELAY_MS + (index === 0 ? 0 : 160);

  if (openedAt == null) {
    return flowDelay;
  }

  return Math.max(0, flowDelay - (performance.now() - openedAt));
};

export function CashEverydayModal({
  visible,
  closing,
  targetAmount,
  selectedGiftIndex,
  cashGiftRevealPhase,
  cashGiftRevealItems,
  showCashGiftIdleEffects = true,
  cashGiftGuideRef,
  showCashGiftGuide,
  cashGiftGuideDismissing = false,
  onChooseGift,
}: CashEverydayModalProps) {
  const {t} = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const firstGiftRef = useRef<HTMLButtonElement>(null);
  const giftOpenedAtRef = useRef<(number | undefined)[]>([]);

  useEffect(() => {
    if (!visible) {
      giftOpenedAtRef.current = [];
    }
  }, [visible]);

  if (!visible && !closing) {
    return null;
  }

  const revealAmounts = cashGiftRevealItems
    .map(item => item.amount)
    .filter((amount): amount is number => amount != null);
  const maxRevealAmount = revealAmounts.length ? Math.max(...revealAmounts) : 0;
  const isRevealing = cashGiftRevealPhase !== 'idle';
  const cashEverydayTitle = t('invitation.referEarn.cashEverydayTitle', {
    amount: `${defaultCurrencySymbol}${Math.trunc(targetAmount)}`,
  });

  return renderModalPortal(
    <div
      ref={overlayRef}
      className={[
        'fixed inset-0 left-1/2 z-[20000] flex h-screen w-full max-w-[500px] -translate-x-1/2 items-center justify-center overflow-hidden bg-[#FFF8DF] bg-cover bg-center bg-no-repeat',
        closing
          ? 'animate-[refer-earn-modal-backdrop-out_320ms_ease-in_forwards]'
          : 'animate-[refer-earn-modal-backdrop-in_220ms_ease-out_forwards]',
      ].join(' ')}
      style={{backgroundImage: `url(${referEarnWhitePageBgImage})`}}>
      <div
        className={[
          'relative h-screen max-h-[41.6875rem] w-full max-w-[23.4375rem] overflow-hidden',
          closing
            ? 'origin-center animate-[refer-earn-cash-modal-collapse_320ms_ease-in_forwards]'
            : 'transform-none [animation:none]',
        ].join(' ')}>
        <div className="absolute left-0 right-0 top-[-2.5rem] h-full">
          <div className="pointer-events-none absolute left-1/2 top-[8.8125rem] flex h-[2.625rem] w-[23.1875rem] -translate-x-1/2 items-center justify-between">
            <img
              src={cashArrowLeftImage}
              alt=""
              className="refer-earn-cash-arrow-left h-[2.625rem] w-[2.75rem] object-contain"
            />
            <AutoFitSingleLineText
              text={cashEverydayTitle}
              containerClassName="h-[2.625rem] w-[18.125rem]"
              className="refer-earn-cash-everyday-title relative -top-[0.125rem] bg-[linear-gradient(180deg,#FF9115_32.17%,#F8A500_46.223%,#FF6250_79.167%)] bg-clip-text text-center [font-family:Rowdies,sans-serif] text-[2rem] font-light leading-[2.625rem] tracking-[0.04rem] text-transparent [filter:drop-shadow(0_0.125rem_0.0375rem_#AB2D22)]"
            />
            <img
              src={cashArrowRightImage}
              alt=""
              className="refer-earn-cash-arrow-right h-[2.625rem] w-[2.75rem] object-contain"
            />
          </div>

          <div className="refer-earn-cash-reward-title absolute left-1/2 top-[12.4375rem] w-[18rem] -translate-x-1/2 whitespace-nowrap bg-[#784204] bg-clip-text text-center font-inter text-base font-semibold leading-[0.875rem] tracking-[-0.04296875rem] text-transparent">
            {t('invitation.wheel.chooseYourReward')}
          </div>

          <div
            className={[
              'absolute left-1/2 -ml-[9.59375rem] grid w-[19.1875rem] grid-cols-2',
              isRevealing
                ? 'top-[15.8125rem] gap-x-[1.21875rem] gap-y-[2.25rem]'
                : 'top-[16.375rem] gap-x-[1.21875rem] gap-y-[1.25rem]',
            ].join(' ')}>
            {Array.from({length: 4}, (_, index) => {
              const giftReveal = cashGiftRevealItems[index];
              const giftOpened = Boolean(giftReveal?.opened);
              if (giftOpened) {
                if (giftOpenedAtRef.current[index] == null) {
                  giftOpenedAtRef.current[index] = performance.now();
                }
              } else {
                giftOpenedAtRef.current[index] = undefined;
              }
              const giftFocused =
                cashGiftRevealPhase === 'focused' &&
                selectedGiftIndex === index;
              const giftHidden =
                cashGiftRevealPhase === 'focused' &&
                selectedGiftIndex !== index;
              const giftAmount = giftReveal?.amount;
              const giftSelecting =
                selectedGiftIndex != null && cashGiftRevealPhase === 'idle';
              const openGiftImage =
                giftAmount != null && giftAmount >= maxRevealAmount
                  ? cashGiftOpenMaxImage
                  : cashGiftOpenMinImage;

              return (
                <button
                  key={index}
                  ref={index === 0 ? firstGiftRef : undefined}
                  type="button"
                  disabled={selectedGiftIndex != null}
                  onClick={() => onChooseGift(index)}
                  className={[
                    'refer-earn-cash-gift-item relative h-[8.5625rem] w-[8.96875rem] cursor-pointer border-0 bg-transparent p-0 disabled:cursor-default',
                    index === 0 && selectedGiftIndex == null ? 'z-[21]' : '',
                    showCashGiftGuide && index !== 0
                      ? 'pointer-events-none'
                      : '',
                    selectedGiftIndex != null &&
                    selectedGiftIndex !== index &&
                    !giftOpened
                      ? 'opacity-70'
                      : '',
                    giftOpened ? 'refer-earn-cash-gift-opened' : '',
                    giftFocused ? 'refer-earn-cash-gift-focused' : '',
                    giftHidden ? 'refer-earn-cash-gift-hidden' : '',
                  ].join(' ')}
                  style={{
                    ['--refer-earn-cash-open-delay' as string]:
                      giftOpened &&
                      cashGiftRevealPhase !== 'selectedOpen' &&
                      selectedGiftIndex !== index
                        ? '160ms'
                        : '0ms',
                  }}>
                  <span
                    className={[
                      'refer-earn-cash-gift-visual absolute inset-0 block',
                      !giftOpened && selectedGiftIndex == null
                        ? 'refer-earn-cash-gift-idle'
                        : '',
                      !giftOpened && giftSelecting
                        ? 'refer-earn-cash-gift-idle-settling'
                        : '',
                    ].join(' ')}>
                    {showCashGiftIdleEffects &&
                    cashGiftRevealPhase === 'idle' &&
                    !giftOpened ? (
                      <span
                        className={[
                          'refer-earn-cash-gift-effects-layer absolute inset-0 block',
                          giftSelecting
                            ? 'refer-earn-cash-gift-effects-dismissing'
                            : '',
                        ].join(' ')}
                        style={{
                          transitionDuration: `${CASH_GIFT_EFFECTS_FADE_MS}ms`,
                        }}>
                        <span className="refer-earn-cash-gift-energy-rise" />
                        <span className="refer-earn-cash-gift-spark refer-earn-cash-gift-spark-a" />
                        <span className="refer-earn-cash-gift-spark refer-earn-cash-gift-spark-b" />
                      </span>
                    ) : null}
                    <img
                      src={cashGiftImage}
                      alt=""
                      className="refer-earn-cash-gift-closed"
                    />
                    <img
                      src={openGiftImage}
                      alt=""
                      className="refer-earn-cash-gift-open"
                    />
                    <span className="refer-earn-cash-gift-open-flash" />
                    {giftOpened ? (
                      <img
                        src={congratulationShineImage}
                        alt=""
                        className="refer-earn-cash-gift-open-shine pointer-events-none absolute left-[-0.9375rem] top-0 z-[2] h-[9.375rem] w-[9.375rem] object-contain"
                      />
                    ) : null}
                  </span>
                  {giftOpened && giftAmount != null && !giftHidden ? (
                    <span
                      className="refer-earn-cash-gift-amount absolute left-1/2 top-[9.3125rem] z-[2] flex h-[1rem] -translate-x-1/2 items-center justify-center whitespace-nowrap font-inter text-sm font-semibold leading-none"
                      style={{
                        animationDelay: `${getCashGiftAmountDelayMs(
                          index,
                          cashGiftRevealPhase,
                          giftOpenedAtRef.current[index],
                        )}ms`,
                      }}>
                      {defaultCurrencySymbol} {formatProgressAmount(giftAmount)}
                    </span>
                  ) : null}
                  {index === 0 && showCashGiftGuide ? (
                    <div
                      ref={cashGiftGuideRef}
                      className={[
                        'refer-earn-cash-gift-guide pointer-events-none absolute left-[55%] top-[60%] z-[22] h-[6.5rem] w-[6.5rem] -translate-x-1/2 -translate-y-1/2',
                        selectedGiftIndex != null || cashGiftGuideDismissing
                          ? 'refer-earn-cash-gift-guide-dismissing'
                          : '',
                      ].join(' ')}
                      style={{
                        transitionDuration: `${CASH_GIFT_GUIDE_DISMISS_MS}ms`,
                      }}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <CashGiftGuideMask
        visible={showCashGiftGuide}
        fading={cashGiftGuideDismissing || selectedGiftIndex != null}
        containerRef={overlayRef}
        holeRef={firstGiftRef}
      />
    </div>,
  );
}
